// PWA client wiring: service-worker registration + update flow, online/offline status, and
// the install prompt. Kept out of main.js so the bootstrap stays thin and the About view
// can subscribe to the same state. The offline DATA path lives elsewhere (the schedule
// renders offline from the idb-keyval cache in store/offline-cache.js); this module owns
// only the PWA platform concerns. Registration is guarded to PRODUCTION builds
// (import.meta.env.PROD) so the Vite dev server / HMR is never disturbed by a controlling
// worker.

import { showToast } from "./components/toast.js";

const networkSubscribers = new Set();

export function isOnline() {
  return navigator.onLine;
}

export function onNetworkChange(cb) {
  networkSubscribers.add(cb);
  return () => networkSubscribers.delete(cb);
}

function notifyNetwork() {
  const online = navigator.onLine;
  document.documentElement.classList.toggle("is-offline", !online);
  for (const cb of networkSubscribers) {
    cb(online);
  }
}

let deferredPrompt = null;

const installSubscribers = new Set();

export function canInstall() {
  return deferredPrompt !== null;
}

export function onInstallAvailabilityChange(cb) {
  installSubscribers.add(cb);
  return () => installSubscribers.delete(cb);
}

function notifyInstall(available) {
  for (const cb of installSubscribers) {
    cb(available);
  }
}

// Single-use: the browser only lets us prompt once per captured event, so we null the
// deferred prompt as we consume it. Resolves true if the user accepted.
export async function promptInstall() {
  if (!deferredPrompt) {
    return false;
  }
  const event = deferredPrompt;
  deferredPrompt = null;
  notifyInstall(false);
  await event.prompt();
  const choice = await event.userChoice;
  return choice.outcome === "accepted";
}

// Show a STICKY toast offering to activate a waiting worker. Clicking "Reload" messages the
// worker to skipWaiting; the `controllerchange` listener below then reloads onto the new SW.
// `duration: 0` keeps it up until the user acts (an update prompt must not vanish on a
// timer). No dedupe guard is needed: the shared toast is single-instance, so a repeat call
// refreshes the same prompt rather than stacking a second one.
function showUpdateToast(waiting) {
  showToast({
    message: "A new version is available.",
    actionLabel: "Reload",
    onAction: () => activateWaiting(waiting),
    duration: 0,
  });
}

// Guard so the page reloads at most once, whichever trigger fires first.
let reloading = false;
function reloadOnce() {
  if (reloading) {
    return;
  }
  reloading = true;
  window.location.reload();
}

// The happy path is `controllerchange` -> reloadOnce, but iOS Safari frequently does NOT
// fire `controllerchange` after `skipWaiting`, leaving the "Reload" doing nothing. A short
// fallback timer forces the reload so the update always applies; the shared guard keeps it
// to a single reload when both fire.
function activateWaiting(waiting) {
  waiting.postMessage({ type: "SKIP_WAITING" });
  setTimeout(reloadOnce, 1500);
}

function reloadOnControllerChange() {
  navigator.serviceWorker.addEventListener("controllerchange", reloadOnce);
}

// Prompt only when a controller already runs, which is what distinguishes an UPDATE from
// the very first install.
function promptUpdate(worker) {
  if (worker && navigator.serviceWorker.controller) {
    showUpdateToast(worker);
  }
}

function watchForUpdate(registration) {
  registration.addEventListener("updatefound", () => {
    const installing = registration.installing;
    if (!installing) {
      return;
    }
    installing.addEventListener("statechange", () => {
      if (installing.state === "installed") {
        promptUpdate(installing);
      }
    });
  });
}

// No-op where the API is unavailable; production-only registration is enforced by the caller.
async function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) {
    return;
  }

  reloadOnControllerChange();

  try {
    const registration = await navigator.serviceWorker.register("/sw.js");
    promptUpdate(registration.waiting); // already waiting from a previous visit?
    watchForUpdate(registration);
  } catch (err) {
    // Registration failure must never break the app; it just means no offline.
    console.warn("Service worker registration failed:", err);
  }
}

// We own the producer (sw.js posts `{ type: "SW_BUILD", version, commit, builtAt }`, all
// strings from its SwBuild), so once the message type checks out the fields are trusted
// as-is. The only real variation is a foreign or empty message, which maps to null; the
// defaults are belt-and-braces for that impossible shape.
function parseBuild(data) {
  const msg =  (data);
  if (!msg || msg.type !== "SW_BUILD") {
    return null;
  }
  const { version = "unknown", commit = "unknown", builtAt = "" } = msg;
  return { version, commit, builtAt };
}

// An older worker that predates the `GET_VERSION` handler never replies, so we resolve null
// after a short timeout; the caller renders that as "unknown", which is itself the tell-tale
// of a stale worker. The timeout and the reply race harmlessly: `resolve` is idempotent, so
// the first outcome wins and there's no double-settle to guard against.
function askWorkerBuild(worker, timeoutMs = 1500) {
  return new Promise((resolve) => {
    const channel = new MessageChannel();
    const timer = setTimeout(() => resolve(null), timeoutMs);
    channel.port1.onmessage = (event) => {
      clearTimeout(timer);
      resolve(parseBuild(event.data));
    };
    // A worker that has gone `redundant` (activation churn on iOS) throws synchronously on
    // `postMessage`; treat that as "no reply" rather than let it reject and leave the /about
    // panel stuck on its "Checking" placeholder.
    try {
      worker.postMessage({ type: "GET_VERSION" }, [channel.port2]);
    } catch {
      clearTimeout(timer);
      resolve(null);
    }
  });
}

// Keeps getServiceWorkerStatus bounded so the panel can never stick on its "Checking"
// placeholder.
function withTimeout(promise, ms, fallback) {
  return Promise.race([promise, new Promise((resolve) => setTimeout(() => resolve(fallback), ms))]);
}

// Never throws and always resolves within a couple of seconds (see withTimeout): on iOS
// Safari the registration lookup could otherwise hang during activation churn, leaving the
// /about panel stuck on "Checking".
export async function getServiceWorkerStatus() {
  const pageCommit = __BUILD_COMMIT__;
  const isDev = !import.meta.env.PROD;
  const base = {
    supported: "serviceWorker" in navigator,
    isDev,
    registered: false,
    active: false,
    controlled: false,
    updateWaiting: false,
    worker: null,
    pageCommit,
    sync: "unknown",
  };
  if (!base.supported) {
    return base;
  }

  base.controlled = navigator.serviceWorker.controller !== null;
  // `getRegistration()` can reject (SecurityError / InvalidStateError during activation
  // churn); swallow it to a timed-out lookup so this function keeps its "never throws,
  // always resolves" contract. A rejection here would otherwise strand /about on "Checking"
  // (the `.then` in the view never fires).
  const registration = await withTimeout(
    navigator.serviceWorker.getRegistration().catch(() => undefined),
    2500,
    undefined,
  );
  if (!registration) {
    // Either genuinely unregistered, or the lookup timed out mid-activation. If a controller
    // is live we still know a worker is in charge, just not its build.
    base.registered = base.controlled;
    base.active = base.controlled;
    return base;
  }

  base.registered = true;
  base.active = registration.active !== null;
  base.updateWaiting = registration.waiting !== null;
  if (registration.active) {
    base.worker = await askWorkerBuild(registration.active);
    // Compare BOTH commit and build time: the commit alone isn't a unique build identity
    // (two dirty builds share the same `<hash>-dirty`), but each build is stamped with a
    // distinct time, so the pair reliably tells same-build apart.
    base.sync = base.worker
      ? base.worker.commit === pageCommit && base.worker.builtAt === __BUILD_TIME__
        ? "match"
        : "mismatch"
      : "unknown";
  }
  return base;
}

const swSubscribers = new Set();
let swListenersAttached = false;

function notifySwChange() {
  for (const cb of swSubscribers) {
    cb();
  }
}

// Attach the lifecycle listeners once, lazily, on the first subscription.
function attachSwListeners() {
  if (swListenersAttached || !("serviceWorker" in navigator)) {
    return;
  }
  swListenersAttached = true;
  // A new worker took control: active build changed.
  navigator.serviceWorker.addEventListener("controllerchange", notifySwChange);
  // A new worker began installing: watch it reach "installed" (i.e. waiting).
  void navigator.serviceWorker.getRegistration().then((registration) => {
    registration?.addEventListener("updatefound", () => {
      registration.installing?.addEventListener("statechange", notifySwChange);
      notifySwChange();
    });
  });
}

// The callback takes no args; re-query getServiceWorkerStatus to refresh.
export function onServiceWorkerChange(cb) {
  attachSwListeners();
  swSubscribers.add(cb);
  return () => swSubscribers.delete(cb);
}

export function initPwa() {
  // Connectivity: reflect immediately and on every change.
  notifyNetwork();
  window.addEventListener("online", notifyNetwork);
  window.addEventListener("offline", notifyNetwork);

  // Capture the install prompt so we can trigger it from our own UI.
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    deferredPrompt =  (event);
    notifyInstall(true);
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    notifyInstall(false);
  });

  // Register the worker for production builds only; never under the dev server.
  if (import.meta.env.PROD) {
    window.addEventListener("load", () => void registerServiceWorker());
  }
}
